import React, { useEffect, useRef } from "react";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import {
  PolicyPageWrapper,
  PolicyHeroSection,
  PolicyContainer,
} from "../Policy/styles";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const PrivacyPolicy = () => {
  const heroRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        hero.querySelector(".heroLabel"),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", delay: 0.1 }
      );

      gsap.fromTo(
        hero.querySelector("h1"),
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.2 }
      );

      gsap.fromTo(
        hero.querySelector(".heroDescription"),
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.4 }
      );
    }, hero);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const ctx = gsap.context(() => {
      const sections = content.querySelectorAll(".content-section");
      sections.forEach((section, index) => {
        gsap.fromTo(
          section,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    }, content);

    return () => ctx.revert();
  }, []);

  return (
    <PolicyPageWrapper>
      <Header />

      <PolicyHeroSection ref={heroRef}>
        <div className="heroBg">
          <div className="gradientOrb orb1" />
          <div className="gradientOrb orb2" />
          <div className="gridPattern" />
        </div>

        <div className="floatingShape shape1" />
        <div className="floatingShape shape2" />
        <div className="floatingShape shape3" />

        <div className="heroContent">
          <div className="heroLabel">
            <span className="line" />
            <span>Legal</span>
            <span className="line" />
          </div>

          <h1>
            Privacy <span className="highlight">Policy</span>
          </h1>

          <p className="heroDescription">
            Your privacy is important to us. This policy explains how we collect,
            use, and protect your personal information.
          </p>
        </div>
      </PolicyHeroSection>

      <PolicyContainer>
        <ContentWrapper ref={contentRef}>
          <ContentSection className="content-section">
            <h2>Information We Collect</h2>
            <p>
              At Sahara Computers, we collect information that you provide directly
              to us when you:
            </p>
            <ul>
              <li>Fill out contact forms or request quotes</li>
              <li>Create an account on our website</li>
              <li>Purchase products or services</li>
              <li>Subscribe to our newsletter</li>
              <li>Contact our customer support</li>
            </ul>
            <p>
              This information may include your name, email address, phone number,
              company name, shipping address, and payment information.
            </p>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Process and fulfill your orders</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Send transactional emails (order confirmations, shipping updates)</li>
              <li>Improve our products and services</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Comply with legal obligations</li>
            </ul>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Information Security</h2>
            <p>
              We implement appropriate technical and organizational security measures
              to protect your personal information against unauthorized access,
              alteration, disclosure, or destruction. These measures include:
            </p>
            <ul>
              <li>SSL encryption for data transmission</li>
              <li>Secure server infrastructure</li>
              <li>Regular security audits and updates</li>
              <li>Limited access to personal information by employees</li>
            </ul>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Cookies and Tracking</h2>
            <p>
              Our website uses cookies and similar tracking technologies to enhance
              your browsing experience. Cookies help us:
            </p>
            <ul>
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our website</li>
              <li>Improve site performance and functionality</li>
              <li>Provide personalized content</li>
            </ul>
            <p>
              You can control cookies through your browser settings. However,
              disabling cookies may affect your experience on our website.
            </p>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Third-Party Services</h2>
            <p>
              We may share your information with trusted third-party service
              providers who assist us in operating our website, conducting our
              business, or serving you. These parties are obligated to keep your
              information confidential and secure.
            </p>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of marketing communications</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
          </ContentSection>

          <ContentSection className="content-section">
            <h2>Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data
              practices, please contact us at:
            </p>
            <ContactInfo>
              <div className="contact-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span>sales@saharacomputers.com</span>
              </div>
            </ContactInfo>
          </ContentSection>

          <LastUpdated>Last updated: January 2024</LastUpdated>
        </ContentWrapper>
      </PolicyContainer>

      <Footer />
    </PolicyPageWrapper>
  );
};

export default PrivacyPolicy;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
`;

const ContentSection = styled.div`
  margin-bottom: 48px;
  will-change: transform, opacity;

  h2 {
    font-family: Poppins;
    font-size: 28px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 20px;
    position: relative;
    padding-left: 20px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 8px;
      width: 4px;
      height: 24px;
      background: var(--accent);
      border-radius: 2px;
    }
  }

  p {
    font-family: Poppins;
    font-size: 16px;
    line-height: 1.8;
    color: var(--text-secondary);
    margin: 0 0 16px;
  }

  ul {
    margin: 0 0 20px;
    padding-left: 24px;

    li {
      font-family: Poppins;
      font-size: 16px;
      line-height: 1.8;
      color: var(--text-secondary);
      margin-bottom: 8px;
      position: relative;

      &::marker {
        color: var(--accent);
      }
    }
  }
`;

const ContactInfo = styled.div`
  margin-top: 20px;

  .contact-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: var(--card-bg);
    border-radius: 12px;
    border: 2px solid var(--card-border);

    svg {
      width: 24px;
      height: 24px;
      color: var(--accent);
    }

    span {
      font-family: Poppins;
      font-size: 16px;
      font-weight: 500;
      color: var(--text-primary);
    }
  }
`;

const LastUpdated = styled.div`
  font-family: Poppins;
  font-size: 14px;
  color: var(--text-secondary);
  text-align: center;
  padding-top: 40px;
  border-top: 1px solid var(--card-border);
`;
